import {
  FlightDataEntry,
  FlightProblems,
  FlightStatus,
  JourneyType,
} from "@/core/types/types";
import getProblemByFlightStatus from "@/utils/getProblemByFlightStatus";
import { PreselectedValue } from "@/components/custom/SelectMultiLegProblem/SelectMultiLegProblem";

interface LegInputData {
  flightProblem: FlightProblems | null;
  originallySelectedProblem: string;
  preselectedValues: PreselectedValue[];
  flightOrder: number;
  dataEntry: FlightDataEntry;
}

type LegPreselectionFunction = (legData: LegInputData) => string | undefined;

/**
 * SingleLeg:
 *  - FDA unknown/no flight data(manual flight) => original flight problem preselected
 *  - OnTime -> no preselection
 *  - Delayed/Cancelled => Delayed/Cancelled preselected accordingly
 * MultiLeg (1st flight):
 *  - FDA unknown/no flight data(manual flight) => no preselection for all legs
 *  - OnTime => OnTime for 1st leg, no preselection for further legs
 *  - Delayed:
 *    - arrived after next leg scheduled departure => first leg delayed, further legs are MissedConnection
 *    - arrived before next leg scheduled departure => first leg delayed, further legs no preselection
 *  - Cancelled => Cancelled for 1st leg, MissedConnection for further legs
 */
const getPreselectedFlightProblems = (
  flightsData: Record<string, FlightDataEntry>,
  journeyType: JourneyType,
  originallySelectedProblem: string
): Record<string, PreselectedValue> => {
  const isSingleLeg = journeyType === JourneyType.SingleLeg;
  const getLegProblem: LegPreselectionFunction = isSingleLeg
    ? getSingleLegPreselection
    : getMultiLegPreselection;

  return Object.entries(flightsData).reduce(
    (
      preselectedProblems: Record<string, PreselectedValue>,
      [routeKey, dataEntry]: [string, FlightDataEntry],
      currentIndex: number
    ) => {
      const flightStatus: FlightStatus = dataEntry?.flightSearchResult
        ?.status as FlightStatus;
      const flightProblem: FlightProblems | null =
        getProblemByFlightStatus(flightStatus);

      const preselectedValue: string | undefined = getLegProblem({
        flightProblem,
        originallySelectedProblem,
        preselectedValues: Object.values(preselectedProblems),
        flightOrder: currentIndex,
        dataEntry,
      });
      const arrivalTime: string | undefined =
        flightProblem == FlightProblems.Delayed
          ? dataEntry?.flightSearchResult?.actualArrival
          : undefined;

      preselectedProblems[routeKey] = {
        flightProblem: preselectedValue,
        arrivalTime,
      };
      return preselectedProblems;
    },
    {}
  );
};

const getSingleLegPreselection = ({
  flightProblem,
  originallySelectedProblem,
}: LegInputData): string | undefined => {
  if (!flightProblem && originallySelectedProblem != FlightProblems.Other) {
    return originallySelectedProblem;
  } else if (
    flightProblem === FlightProblems.Delayed ||
    flightProblem === FlightProblems.Cancelled
  ) {
    return flightProblem;
  }

  return undefined;
};

const getMultiLegPreselection = ({
  flightProblem,
  preselectedValues,
  flightOrder,
  dataEntry,
}: LegInputData): string | undefined => {
  const firstLegProblem = preselectedValues[0]?.flightProblem as FlightProblems;
  const previousLegProblem = preselectedValues[flightOrder - 1]
    ?.flightProblem as FlightProblems;
  const legIsMissed =
    (firstLegProblem === FlightProblems.Delayed &&
      dataEntry?.connectionIsMissed) ||
    firstLegProblem === FlightProblems.Cancelled;

  if (!firstLegProblem && flightOrder === 0) {
    return flightProblem ?? undefined;
  } else if (
    legIsMissed ||
    previousLegProblem === FlightProblems.MissedConnection
  ) {
    return FlightProblems.MissedConnection;
  }

  return undefined;
};

export default getPreselectedFlightProblems;
